import { cn } from "@/lib/utils";
import { User, Bot, CheckCircle2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import type { AgentChatMessage, ToolResult } from "@/hooks/useAgentChat";
import { useTranslation } from "react-i18next";
import { ActionFeedbackCard } from "@/components/chat/ActionFeedbackCard";
import { ErrorCard } from "@/components/chat/ErrorCard";
import { InlineToolEvent } from "@/components/chat/InlineToolEvent";
import { WRITE_TOOLS } from "@/lib/chat-utils";

interface ChatMessageProps {
  message: AgentChatMessage;
  onQuickAction?: (text: string) => void;
  onRetry?: () => void;
}

function ToolBadge({ result }: { result: ToolResult }) {
  const { t } = useTranslation();

  const toolLabels: Record<string, { icon: typeof CheckCircle2; label: string; color: string }> = {
    query_inspections: {
      icon: CheckCircle2,
      label: t("inspectionChat.tools.context"),
      color: "bg-zinc-500/10 text-zinc-500 dark:text-zinc-400",
    },
    query_defects: {
      icon: CheckCircle2,
      label: t("inspectionChat.tools.context"),
      color: "bg-zinc-500/10 text-zinc-500 dark:text-zinc-400",
    },
  };

  const config = toolLabels[result.tool] || {
    icon: CheckCircle2,
    label: result.tool,
    color: "bg-zinc-500/10 text-zinc-500",
  };
  const Icon = config.icon;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-normal",
        config.color,
        !result.success && "bg-destructive/10 text-destructive",
      )}
    >
      <Icon className="h-3 w-3" />
      {result.success ? config.label : t("inspectionChat.tools.failed")}
    </span>
  );
}

type QuickAction = { label: string; action: string; kind: "checklist" | "continue" };

/** Quick actions from a single list_checklists tool result. */
function quickActionsFromResult(result: Record<string, unknown>): QuickAction[] {
  const checklists = (result.checklists as Array<{ name: string }> | undefined) ?? [];
  const openInspections =
    (result.openInspections as Array<{ checklistName: string }> | undefined) ?? [];

  return [
    ...checklists.map((cl) => ({ label: cl.name, action: cl.name, kind: "checklist" as const })),
    // label = checklist name (data, language-neutral); the German "fortsetzen" lives only in
    // `action`, the re-submit text for the German-context agent. The ▶ icon signals "continue".
    ...openInspections.map((insp) => ({
      label: insp.checklistName,
      action: `${insp.checklistName} fortsetzen`,
      kind: "continue" as const,
    })),
  ];
}

/** Extract clickable items from list_checklists tool results. */
function extractQuickActions(toolResults?: ToolResult[]): QuickAction[] {
  return (toolResults ?? [])
    .filter((tr) => tr.tool === "list_checklists" && tr.success)
    .flatMap((tr) => quickActionsFromResult(tr.result));
}

// Hidden tool names — not shown to users
const HIDDEN_TOOLS = new Set([
  "get_context",
  "get_next_item",
  "list_checklists",
  "list_team_members",
]);

// Read-only tools shown as ToolBadge pills
const READ_TOOLS = new Set(["query_inspections", "query_defects"]);

/** Read-tool badge pills (query_inspections / query_defects). */
function ReadToolBadges({ results }: { results: ToolResult[] }) {
  if (results.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {results.map((tr, idx) => (
        <ToolBadge key={idx} result={tr} />
      ))}
    </div>
  );
}

/** Write-tool action feedback cards. */
function WriteToolCards({
  results,
  onNextAction,
}: {
  results: ToolResult[];
  onNextAction?: (text: string) => void;
}) {
  if (results.length === 0) return null;
  return (
    <div className="space-y-2">
      {results.map((tr, idx) => (
        <ActionFeedbackCard key={idx} toolResult={tr} onNextAction={onNextAction} />
      ))}
    </div>
  );
}

/** In-progress tool-call badges shown during a streaming turn. */
function ActiveToolCalls({ message }: { message: AgentChatMessage }) {
  const active = message.activeToolCalls ?? [];
  if (!message.isStreaming || active.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {active.map((tc) => (
        <InlineToolEvent key={tc.toolCallId} toolCall={tc} />
      ))}
    </div>
  );
}

/** Clickable quick-action chips (checklists / open inspections). */
function QuickActionChips({
  actions,
  onQuickAction,
}: {
  actions: QuickAction[];
  onQuickAction?: (text: string) => void;
}) {
  if (actions.length === 0 || !onQuickAction) return null;
  return (
    <div className="flex flex-wrap gap-1.5 pt-1">
      {actions.map((qa, idx) => (
        <Button
          key={idx}
          variant="outline"
          size="sm"
          onClick={() => onQuickAction(qa.action)}
          className="h-7 rounded-full text-xs gap-1"
        >
          {qa.kind === "continue" ? "▶" : "📋"} {qa.label}
        </Button>
      ))}
    </div>
  );
}

/** The message bubble (markdown for assistant, plain text for user). */
function MessageBubble({ message, isUser }: { message: AgentChatMessage; isUser: boolean }) {
  return (
    <div
      className={cn(
        "rounded-2xl px-4 py-2 text-sm",
        isUser
          ? "bg-primary text-primary-foreground rounded-br-md"
          : "bg-muted text-foreground rounded-bl-md",
      )}
    >
      {isUser ? (
        <p className="whitespace-pre-wrap">{message.content}</p>
      ) : (
        <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

function ChatAvatar({ isUser }: { isUser: boolean }) {
  return (
    <div
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors",
        isUser ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
      )}
    >
      {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
    </div>
  );
}

/** Error messages render as a single ErrorCard with retry/alternatives. */
function ChatErrorMessage({ message, onQuickAction, onRetry }: ChatMessageProps) {
  return (
    <div className="flex gap-3 flex-row">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Bot className="h-4 w-4" />
      </div>
      <div className="max-w-[85%] space-y-1.5">
        <ErrorCard
          errorMessage={message.content}
          errorCode={message.errorCode}
          onRetry={onRetry}
          onAlternativeAction={onQuickAction}
        />
      </div>
    </div>
  );
}

export function ChatMessage({ message, onQuickAction, onRetry }: ChatMessageProps) {
  if (message.isError) {
    return <ChatErrorMessage message={message} onQuickAction={onQuickAction} onRetry={onRetry} />;
  }

  const isUser = message.role === "user";
  const quickActions = isUser ? [] : extractQuickActions(message.toolResults);
  const allResults = message.toolResults ?? [];
  const writeTr = allResults.filter((tr) => WRITE_TOOLS.has(tr.tool));
  const readTr = allResults.filter((tr) => READ_TOOLS.has(tr.tool) && !HIDDEN_TOOLS.has(tr.tool));

  return (
    <div className={cn("flex gap-3", isUser ? "flex-row-reverse" : "flex-row")}>
      <ChatAvatar isUser={isUser} />
      <div
        className={cn("max-w-[85%] space-y-1.5", isUser && "flex flex-col items-end")}
        aria-live={!isUser && message.isStreaming ? "polite" : undefined}
      >
        <ReadToolBadges results={readTr} />
        <WriteToolCards results={writeTr} onNextAction={onQuickAction} />
        <MessageBubble message={message} isUser={isUser} />
        <ActiveToolCalls message={message} />
        <QuickActionChips actions={quickActions} onQuickAction={onQuickAction} />
      </div>
    </div>
  );
}
